import {
  getPageHeader,
  managedEmployerOrganisationSwitcherEnabled,
  getPluginSlots,
  useEmployerOrganisationOptional,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  OnboardingSetupCard,
  useOrganisations,
  navigateWithThreadPreservation,
  DashboardMetricCard,
  DashboardMetricGrid,
  DashboardSectionAction,
  DashboardSectionTitle,
  DashboardSectionSkeleton,
  DashboardStatsSkeleton,
  PaneResponsiveCardGrid,
  PersonListCard,
  employerRecentApplicantToPersonModel,
  MatchQualityBadge,
  countLabel,
  getDashboardSection,
} from '@yeshuman/sdk'
import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, Briefcase, Users, Clock, Calendar, ClipboardList } from 'lucide-react'
import { CandidateHomeSections } from '../../components/candidate/candidate-dashboard-home-metrics'
import { ProfileVisibilityNotice } from '../../components/candidate/profile-visibility-notice'
import { useOrganisationOpportunities } from '../../hooks/use-organisation-opportunities'
import { RecruiterDashboardSections } from '../../components/recruiter/recruiter-dashboard-sections'
import { useManagedOrgRecentApplicants } from '../../hooks/use-managed-org-recent-applicants'
import { useMyApplications } from '../../hooks/use-my-applications'
import { InterviewerDashboardSections } from '../../components/interviewer/interviewer-dashboard-sections'

export function CandidateDashboard({ onStartConversation }: { onStartConversation?: (message: string) => void }) {
  if (getPluginSlots().candidateHome === 'sections') {
    return (
      <PageContainer>
        <PageHeader leading={<PageHeaderLeading />} />
        <OnboardingSetupCard className="mb-6" onAskAgent={onStartConversation} />
        <ProfileVisibilityNotice className="mb-6" />
        <CandidateHomeSections />
      </PageContainer>
    )
  }

  return <DefaultCandidateDashboard onStartConversation={onStartConversation} />
}

function DefaultCandidateDashboard({ onStartConversation }: { onStartConversation?: (message: string) => void }) {
  const {
    applications,
    isLoading: isLoadingApplications,
    error: applicationsError,
  } = useMyApplications()

  const applicationCounts = useMemo(() => {
    const counts = {
      applied: 0,
      in_review: 0,
      interview: 0,
      offer: 0,
      hired: 0,
      rejected: 0,
      total: applications.length,
    }

    applications.forEach((app) => {
      const status = app.status as keyof typeof counts
      if (status in counts && status !== 'total') {
        counts[status]++
      }
    })

    return counts
  }, [applications])

  return (
    <PageContainer>
      <PageHeader leading={<PageHeaderLeading />} />
      <OnboardingSetupCard className="mb-6" onAskAgent={onStartConversation} />

      {isLoadingApplications ? (
        <DashboardStatsSkeleton count={4} maxColumns={4} columnLayout="density" />
      ) : applicationsError ? (
        <Card>
          <CardContent>
            <div className="text-center py-4 text-muted-foreground">
              <p>Unable to load application data</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <DashboardMetricGrid maxColumns={4} columnLayout="density">
          <DashboardMetricCard title="Applications" value={applicationCounts.total} icon={Briefcase} />
          <DashboardMetricCard
            title="Pending"
            value={applicationCounts.applied}
            icon={Clock}
          />
          <DashboardMetricCard title="In review" value={applicationCounts.in_review} icon={Search} />
          <DashboardMetricCard
            title={countLabel(applicationCounts.interview, 'Interview', 'Interviews')}
            value={applicationCounts.interview}
            icon={Calendar}
          />
        </DashboardMetricGrid>
      )}

      <div className="grid grid-cols-1 @2col:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>What you can do now</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {(getDashboardSection('candidate')?.capabilities ?? [
                'Browse jobs and apply to roles',
                'Track your application status and history',
                'Run AI-powered job analysis',
                'Manage conversation threads and search through your chat history',
                'View and manage memories that the AI has learned about you'
              ]).map((item: string, i: number) => (
                <li key={i}>• {item}</li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coming soon</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {(getDashboardSection('candidate')?.comingSoon ?? [
                'Resume builder and optimization',
                'Interview practice and coaching',
                'Personalized career recommendations'
              ]).map((item: string, i: number) => (
                <li key={i}>• {item}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  )
}

export function EmployerDashboard({ onStartConversation }: { onStartConversation?: (message: string) => void }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { organisations, isLoading: orgsLoading, error: orgsError } = useOrganisations()
  const empOrgCtx = useEmployerOrganisationOptional()
  const contextDrivenOrg = managedEmployerOrganisationSwitcherEnabled() && !!empOrgCtx?.enabled
  const [localSelectedOrganisation, setLocalSelectedOrganisation] = useState<string>('')

  const selectedOrganisation = contextDrivenOrg ? (empOrgCtx?.activeOrganisationSlug ?? '') : localSelectedOrganisation
  const { opportunities, isLoading: oppsLoading } = useOrganisationOpportunities(selectedOrganisation)

  const {
    applicants: orgRecentApplicants,
    isLoading: recentApplicantsLoading,
    error: recentApplicantsError,
  } = useManagedOrgRecentApplicants(selectedOrganisation || undefined, 25)

  const previewApplicants = orgRecentApplicants.slice(0, 4)
  const matchingProfilesLoading =
    orgsLoading || (!!selectedOrganisation && recentApplicantsLoading)

  // Auto-select first org when available
  useEffect(() => {
    if (contextDrivenOrg) return
    if (organisations && organisations.length > 0 && !localSelectedOrganisation) {
      setLocalSelectedOrganisation(organisations[0].slug)
    }
  }, [contextDrivenOrg, organisations, localSelectedOrganisation])

  return (
    <PageContainer>
      <PageHeader leading={<PageHeaderLeading />} />
      <OnboardingSetupCard className="mb-6" onAskAgent={onStartConversation} />

      {orgsLoading ? (
        <DashboardStatsSkeleton count={2} maxColumns={2} />
      ) : orgsError ? (
        <Card>
          <CardContent>
            <div className="text-center py-4 text-muted-foreground">
              <p>Unable to load organisations</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <DashboardMetricGrid maxColumns={2}>
          <DashboardMetricCard
            title={countLabel(opportunities.length, 'Job opening', 'Job openings')}
            value={organisations.length > 0 ? opportunities.length : 0}
            icon={Briefcase}
            loading={Boolean(organisations.length > 0 && selectedOrganisation && oppsLoading)}
          />
          <DashboardMetricCard
            title={countLabel(orgRecentApplicants.length, 'Recent applicant', 'Recent applicants')}
            value={
              organisations.length === 0
                ? 0
                : !getPluginSlots().employerRecentApplicants || recentApplicantsError
                  ? '—'
                  : orgRecentApplicants.length
            }
            description={
              getPluginSlots().employerRecentApplicants && !recentApplicantsError && orgRecentApplicants.length > 0
                ? 'Last 25'
                : undefined
            }
            icon={Users}
            loading={
              organisations.length > 0 &&
              getPluginSlots().employerRecentApplicants &&
              matchingProfilesLoading &&
              orgRecentApplicants.length === 0 &&
              !recentApplicantsError
            }
          />
        </DashboardMetricGrid>
      )}

      {getPluginSlots().employerMatchingProfiles ? (
        orgsLoading || matchingProfilesLoading ? (
          <DashboardSectionSkeleton cards={4} grid />
        ) : (
        <section className="space-y-4" aria-labelledby="matching-profiles-heading">
          <DashboardSectionTitle
            id="matching-profiles-heading"
            title="Matching profiles"
            action={
              <DashboardSectionAction
                onClick={() =>
                  navigateWithThreadPreservation(navigate, '/employer/jobs', searchParams)
                }
              >
                See all
              </DashboardSectionAction>
            }
          />

          <div className="space-y-4">
            {!selectedOrganisation ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
                <Briefcase className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
                <p className="text-sm font-medium">No organisations yet</p>
                <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                  Create or join an organisation to see applicants here.
                </p>
              </div>
            ) : recentApplicantsError ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-center text-sm">
                <p className="font-medium text-destructive">Could not load applicants</p>
                <p className="mt-1 text-muted-foreground">
                  {(recentApplicantsError as Error).message ?? 'Something went wrong.'}
                </p>
              </div>
            ) : previewApplicants.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
                <Search className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
                <p className="text-sm font-medium">No applicants yet</p>
                <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                  Share postings, invite candidates or run discovery to fill your pipeline.
                </p>
                <div className="mt-4">
                  <DashboardSectionAction
                    onClick={() =>
                      navigateWithThreadPreservation(navigate, '/employer/jobs', searchParams)
                    }
                  >
                    See all
                  </DashboardSectionAction>
                </div>
              </div>
            ) : (
              <PaneResponsiveCardGrid>
                {previewApplicants.map((a) => {
                  return (
                  <PersonListCard
                    key={a.id}
                    variant="compact"
                    className="text-sm"
                    person={employerRecentApplicantToPersonModel(a)}
                    onOpen={() =>
                      navigateWithThreadPreservation(
                        navigate,
                        `/employer/jobs/${a.opportunity_id}/applicants/${a.id}`,
                        searchParams,
                      )
                    }
                    openAriaLabel={`Open applicant ${a.profile.name}`}
                    headerAside={
                      typeof a.match_score === 'number' ? (
                        <MatchQualityBadge score={a.match_score} className="shrink-0" />
                      ) : (
                        <span className="shrink-0 text-xs font-medium text-muted-foreground">
                          Applied
                        </span>
                      )
                    }
                  />
                )})}
              </PaneResponsiveCardGrid>
            )}
          </div>
        </section>
        )
      ) : null}

      {!getPluginSlots().employerMatchingProfiles ? (
      <div className="grid grid-cols-1 @2col:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>What you can do now</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {(getDashboardSection('employer')?.capabilities ?? [
                'Manage your organisations and teams',
                'Post and manage jobs',
                'Run analysis to find matching talent',
                'Manage conversation threads and search through your chat history',
                'View and manage memories that the AI has learned about you'
              ]).map((item: string, i: number) => (
                <li key={i}>• {item}</li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coming soon</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {(getDashboardSection('employer')?.comingSoon ?? [
                'Advanced hiring pipeline analytics',
                'Interview scheduling and coordination',
                'Team roles and permissions management'
              ]).map((item: string, i: number) => (
                <li key={i}>• {item}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
      ) : null}
    </PageContainer>
  )
}

export function RecruiterDashboard({ onStartConversation }: { onStartConversation?: (message: string) => void }) {
  return (
    <PageContainer>
      <OnboardingSetupCard className="mb-6" onAskAgent={onStartConversation} />
      <PageHeader
        leading={<PageHeaderLeading />}
        {...getPageHeader('/recruiter', 'Recruiter Dashboard', 'Connect the right talent with the right jobs')}
        icon={<Users className="h-5 w-5" />}
      />
      <RecruiterDashboardSections />
    </PageContainer>
  )
}

export function InterviewerDashboard({ onStartConversation }: { onStartConversation?: (message: string) => void }) {
  return (
    <PageContainer>
      <OnboardingSetupCard className="mb-6" onAskAgent={onStartConversation} />
      <PageHeader
        leading={<PageHeaderLeading />}
        {...getPageHeader('/interviewer', 'Home', 'People to interview and upcoming bookings')}
        icon={<ClipboardList className="h-5 w-5" />}
      />
      <InterviewerDashboardSections />
    </PageContainer>
  )
}
